'use client';

import { useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { Container } from '@/atoms/Container/Container';
import { DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel } from '@/atoms/DropdownMenu/DropdownMenu';
import { Skeleton } from '@/atoms/Skeleton/Skeleton';
import { PINAKES_RELATED_LIMIT } from '@/config/pinakes';
import { usePinakesRelated } from '@/hooks/usePinakesRelated/usePinakesRelated';
import { cn } from '@/libs/utils/utils';
import { getDisplayLink, getResourceLink, getResourceListNote } from './ResourceCard.utils';
import { ResourceMenuRow } from './ResourceMenuRow';

interface ResourceSimilarProps {
  /** The card's Pinakes ID (`card.id`), what `related=` is asked for. */
  id: string;
  /**
   * The live region the list's loading, empty and error messages are announced in. The Open in
   * menu's owner keeps it mounted beside its button, outside the menu.
   */
  announcer: HTMLElement | null;
}

/**
 * "Similar": the last entry of a card's Open in menu. Choosing it unfolds, inside the same menu,
 * what the people who tagged the resource also tagged (`GET /v1/search?related=<id>`), up to
 * `PINAKES_RELATED_LIMIT` rows of favicon, title and type; choosing it again folds it. The list
 * stays in the menu rather than in a submenu beside it, which would not fit next to the menu on a
 * phone. Read only when unfolded: the list mounts with it, so a feed of cards (or opening Open in)
 * spends none of Pinakes's per-IP budget on it.
 *
 * The messages are announced in `announcer`, outside the menu: a menu may own only items, and a
 * region that appears with its text is often not read.
 *
 * Similar ends a menu that may scroll (a long Also on list, or any menu opened low on a phone), so
 * the list scrolls itself into sight when it unfolds; its loading state reserves one row per
 * result, so the rows that arrive take the place it showed.
 */
export function ResourceSimilar({ id, announcer }: ResourceSimilarProps) {
  const listId = useId();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <DropdownMenuItem
        aria-expanded={isOpen}
        aria-controls={isOpen ? listId : undefined}
        data-testid="resource-similar-trigger"
        className="hover:bg-accent focus:bg-accent"
        // Keeps the Open in menu open: the list unfolds below.
        onSelect={(event) => {
          event.preventDefault();
          setIsOpen(!isOpen);
        }}
      >
        Similar
        <ChevronDown aria-hidden="true" className={cn('ml-auto size-4', isOpen && 'rotate-180')} />
      </DropdownMenuItem>
      {isOpen && <SimilarList id={id} announcer={announcer} listId={listId} />}
    </>
  );
}

function SimilarList({ id, announcer, listId }: ResourceSimilarProps & { listId: string }) {
  const labelId = useId();
  const { data, isLoading, isError } = usePinakesRelated(id);
  const isEmpty = !data || data.length === 0;
  const message = isLoading
    ? 'Loading similar links'
    : isError
      ? 'Similar links could not be loaded'
      : isEmpty
        ? 'Nothing similar yet'
        : null;

  return (
    <DropdownMenuGroup ref={scrollIntoSight} id={listId} aria-labelledby={labelId} data-testid="resource-similar-menu">
      {announcer && message && createPortal(message, announcer)}
      <DropdownMenuLabel id={labelId} className="text-xs font-medium text-muted-foreground">
        People who tagged this also tagged
      </DropdownMenuLabel>
      {isLoading ? (
        <Container aria-hidden="true" data-testid="resource-similar-loading" className="gap-2 px-2 py-1.5">
          {Array.from({ length: PINAKES_RELATED_LIMIT }, (_, index) => (
            <Container key={index} className="flex-row items-center gap-2">
              <Skeleton className="size-4 shrink-0" />
              <Skeleton className="h-4 flex-1" />
            </Container>
          ))}
        </Container>
      ) : isError || isEmpty ? (
        // A disabled item, so the list still has an item to read when it has no rows.
        <DropdownMenuItem
          disabled
          data-testid={isError ? 'resource-similar-error' : 'resource-similar-empty'}
          className="text-muted-foreground data-[disabled]:opacity-100"
        >
          {message}
        </DropdownMenuItem>
      ) : (
        // Calendar occurrences share their feed's `id`: the index keeps the keys unique.
        data.slice(0, PINAKES_RELATED_LIMIT).map((card, index) => {
          const href = getResourceLink(card);
          return (
            <ResourceMenuRow
              key={`${index}-${card.id}`}
              uri={href ?? card.uri}
              href={href}
              name={card.name || getDisplayLink(card.uri)}
              note={getResourceListNote(card, href)}
              testId="resource-similar-row"
            />
          );
        })
      )}
    </DropdownMenuGroup>
  );
}

/**
 * Brings the unfolded list into the menu's view once, as it mounts: a module-level callback keeps
 * one identity, so React does not call it again on each render (scrollIntoView is absent in jsdom,
 * hence the optional call).
 */
function scrollIntoSight(node: HTMLElement | null) {
  node?.scrollIntoView?.({ block: 'nearest' });
}
